import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { TreeNode } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DropdownLazyLoadEvent, DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { TreeSelectModule } from 'primeng/treeselect';
import { Subject, takeUntil } from 'rxjs';

import { GetNoteParams, NoteStatus } from '@core/models/interfaces/report';
import { UserDetail } from '@core/models/interfaces/user';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CustomCalendarComponent } from '@shared/components/custom-calendar/custom-calendar.component';
import { FormControlComponent } from '@shared/components/form-control/form-control.component';
import { NoteService } from '@core/services/note.service';
import { UserFormDropdownComponent } from '@shared/components/dropdown/user-form-dropdown/user-form-dropdown.component';
import { ReportField, requiredExportFieldsNoteKey } from '@core/models/interfaces/report-field';
import { ReportService } from '@core/services/report.service';
import { ReportFieldSelectionComponent } from '@shared/components/report-field-selection/report-field-selection.component';
import { ReportTabEnum } from '@core/enum/report';
@Component({
  selector: 'app-note-selection',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MultiSelectModule,
    CalendarModule,
    TranslateModule,
    DropdownModule,
    TreeSelectModule,
    FormControlComponent,
    ButtonComponent,
    CustomCalendarComponent,
    UserFormDropdownComponent,
    ReportFieldSelectionComponent
  ],
  templateUrl: './note-selection.component.html',
  styleUrl: './note-selection.component.scss'
})
export class NoteSelectionComponent implements OnInit, OnChanges {
  @Input({ required: true }) title = '';
  @Input() isLoading: boolean = false;
  @Input() params: GetNoteParams;
  @Input() isRunning: boolean = false;

  @Input() userGroups: TreeNode[] = [];
  @Output() paramsChange = new EventEmitter<GetNoteParams>();
  @Output() isRunningChange = new EventEmitter<boolean>();

  readonly translatePrefix = 'report.';
  readonly dateNow: Date = new Date();

  performanceForm = this.fb.group({
    users: this.fb.control<UserDetail[]>([]),
    groupBy: this.fb.control('ALL'),
    types: this.fb.control([''])
  });

  dates: Array<Date | undefined> = [];
  noteTypes: NoteStatus[] = [];
  totalNotes: number = 0;
  notePage: number = 0;

  groupByOptions: { label: string; value: string }[] = [
    { label: 'All', value: 'ALL' },
    { label: 'Owner', value: 'OWNER' },
    { label: 'Agent', value: 'AGENT' },
    { label: 'Lead', value: 'LEAD' }
  ];


  private unsubscribe$ = new Subject<void>();
  exportSelectedFields: ReportField[] = []
  isSelectionDialogVisible: boolean = false;
  isAllExportFieldsSelected: boolean = true;

  constructor(
    private fb: FormBuilder,
    private noteService: NoteService,
    private reportService: ReportService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.fetchExportSelectedFields();
    // Nếu đã có params (VD: load lại từ URL) thì đồng bộ form ngay lần khởi tạo.
    if (this.params) {
      this.syncFormFromParams(this.params);
    }

    this.fetchNoteTypes('SEARCH');
  }

  get usersValue() {
    return this.performanceForm.value.users?.filter(item => !!item);
  }

  get groupByValue() {
    return this.performanceForm.value.groupBy;
  }

  /**
   * Lắng nghe thay đổi @Input params (khi ReportComponent map từ queryParams).
   * Đảm bảo multi-select, groupBy và date hiển thị đúng theo URL.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['params']?.currentValue) {
      this.syncFormFromParams(changes['params'].currentValue as GetNoteParams);
    }
  }

  /**
   * Đồng bộ form, dates và exportSelectedFields từ GetNoteParams.
   * Giống cách fix tab Production: sync exportSelectedFields và trim fullName khi back về.
   */
  private syncFormFromParams(params: GetNoteParams) {
    // Trim fullName để tránh khoảng trắng thừa (giống fix tab Production).
    const sanitizedUsers = params.users?.map(u => ({
      ...u,
      fullName: u.fullName?.trim() || ''
    })) || [];

    this.performanceForm.patchValue({
      users: sanitizedUsers.length ? sanitizedUsers : [],
      groupBy: params.groupBy || 'ALL',
      // types trong form đang giữ mảng id noteType -> map trực tiếp từ noteTypeIds
      types: params.noteTypeIds || []
    });

    this.dates = [
      params.createdFrom ? new Date(params.createdFrom) : undefined,
      params.createdTo ? new Date(params.createdTo) : undefined
    ].filter(item => !!item);

    // Đồng bộ exportSelectedFields từ params.visibleFields (khi back về từ page khác).
    if (params.visibleFields?.length && this.exportSelectedFields?.length) {
      const selectedFromParams = params.visibleFields;
      this.exportSelectedFields.forEach(e => {
        e.isSelected = !!selectedFromParams?.find(f => f === e.key);
      });
      this.onExportFieldChanged();
    }
  }

  onRunReport() {
    const value = this.performanceForm.value;
    const createdFrom = this.dates?.[0] ? moment(this.dates?.[0]).toDate().toISOString() : undefined;
    const createdTo = this.dates?.[1] ? moment(this.dates?.[1]).toDate().toISOString() : undefined;

    this.params = {
      userIds: value.users?.map(item => item.id) || [],
      users: value.users || [],
      groupBy: value.groupBy || 'ALL',
      createdFrom,
      noteTypeIds: value.types || [],
      createdTo,
      visibleFields: this.exportSelectedFieldsList(),
    };

    this.paramsChange.emit(this.params);

    this.isRunning = true;
    this.isRunningChange.emit(this.isRunning);
  }

  getTreeNodeValue(value: TreeNode[]) {
    return value
      ?.filter(item => !!item.label)
      ?.map(item => item.label)
      .join(', ');
  }

  getNoteTypesValue(value: NoteStatus[]) {
    return value?.map(item => item.name).join(', ');
  }

  onReset() {
    this.performanceForm.patchValue({
      users: [],
      groupBy: 'ALL',
      types: undefined
    });
    this.dates = [];
    this.isAllExportFieldsSelected = true;
    this.exportSelectedFields.forEach(e => e.isSelected = true)
    this.onExportFieldChanged()
  }

  fetchNoteTypes(type: 'SCROLL' | 'SEARCH') {
    this.noteService.getNoteStatuses({ page: this.notePage }).subscribe({
      next: res => {
        if (type === 'SCROLL') {
          this.noteTypes = [...this.noteTypes, ...res.data.content];
        } else {
          this.noteTypes = res.data.content;
        }
        this.totalNotes = res.data.total;
      }
    });
  }

  onLoadMoreNoteTypes(event: DropdownLazyLoadEvent) {
    if (this.isLoading) return;
    const { last } = event;
    if (last >= this.noteTypes?.length && this.noteTypes?.length < this.totalNotes) {
      this.notePage += 1;
      this.fetchNoteTypes('SCROLL');
    }
  }

  fetchExportSelectedFields() {
    return this.reportService
      .getNoteReportFields()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (result) => {
          this.exportSelectedFields = result.data as ReportField[]
          if (this.params) {
            const selectedFromParams = this.params.visibleFields;
            this.exportSelectedFields.forEach(e => {
              if (selectedFromParams?.find(f => f === e.key)) {
                e.isSelected = true;
              } else {
                e.isSelected = false
              }
            })
          } else {
            this.exportSelectedFields.forEach(e => {
              e.isSelected = true;
            })
          }
          this.onExportFieldChanged()
        },
        error: () => { }
      })

  }

  toggleAllExportFields(isSelected: boolean) {
    this.isAllExportFieldsSelected = isSelected;
    if (this.isAllExportFieldsSelected) {
      this.exportSelectedFields.forEach(e => e.isSelected = true)
    } else {
      this.exportSelectedFields.forEach(e => {
        if (requiredExportFieldsNoteKey.includes(e.key)) {
          e.isSelected = true;
        } else {
          e.isSelected = false
        }
      })
    }
  }

  onExportFieldChanged() {
    this.isAllExportFieldsSelected =
      this.exportSelectedFields.length > 0 &&
      this.exportSelectedFields.every(e => e.isSelected === true)
    this.cdr.markForCheck()
  }

  get requiredExportFieldsNoteKey() {
    return requiredExportFieldsNoteKey
  }

  get exportSelectedFieldsText(): string {
    return `Select fields (${this.isAllExportFieldsSelected ? 'All' : this.exportSelectedFieldsList().length})`
  }

  exportSelectedFieldsList() {
    return this.exportSelectedFields.filter(e => e.isSelected === true).map(e => e.key)
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
